/**
 * Sign in with Apple, Google and Microsoft, through Cognito — `ACC-06`, ADR 0012.
 *
 * The flow is OAuth's authorization code with PKCE, against the Cognito domain:
 *
 *  1. `authorizeUrl` sends the browser to `/oauth2/authorize` with
 *     `identity_provider` set, which goes straight to the provider — nobody sees a
 *     Cognito page (ADR 0012 decision 3).
 *  2. The provider returns to Cognito, and Cognito returns to `/auth/callback`
 *     with a one-time `code`.
 *  3. `exchangeCode` swaps it at `/oauth2/token` for the same three tokens SRP
 *     sign-in produces, which the Cognito client then stores exactly as it stores
 *     those — so everything after sign-in, refresh included, is one code path.
 *
 * **PKCE, because the app client has no secret** and cannot keep one. The code is
 * useless without the verifier, which never leaves this browser's session storage.
 * **`state`, because the callback must be ours**: a code arriving with a state this
 * tab did not issue is refused, which is what stops somebody else's sign-in being
 * completed into this browser.
 *
 * Pure functions over explicit inputs, so the flow is testable without a pool.
 */

export type FederatedProvider = 'apple' | 'google' | 'microsoft'

export const PROVIDER_LABELS: Record<FederatedProvider, string> = {
  apple: 'Apple',
  google: 'Google',
  microsoft: 'Microsoft'
}

/**
 * Every way into a pool through `/oauth2/authorize`: the three customer
 * providers, and `staff` — Match Point's Entra tenant, on the staff pool
 * (ADR 0016). Kept out of `FederatedProvider` on purpose, so it can never be
 * offered as a fourth customer button.
 */
export type SignInRoute = FederatedProvider | 'staff'

/** What Cognito calls each route in `identity_provider`. */
const COGNITO_PROVIDER_NAMES: Record<SignInRoute, string> = {
  apple: 'SignInWithApple',
  google: 'Google',
  microsoft: 'Microsoft',
  staff: 'Entra'
}

const isSignInRoute = (value: unknown): value is SignInRoute =>
  typeof value === 'string' && Object.hasOwn(COGNITO_PROVIDER_NAMES, value)

/** The providers this build can actually sign somebody in with. */
export function liveProviders (value: string | undefined = import.meta.env.VITE_FEDERATED_PROVIDERS): FederatedProvider[] {
  return (value ?? '')
    .split(',')
    .map(p => p.trim().toLowerCase())
    .filter((p): p is FederatedProvider => p in PROVIDER_LABELS)
}

// ── PKCE ────────────────────────────────────────────────────────────────────

const base64Url = (bytes: Uint8Array): string =>
  btoa(String.fromCharCode(...bytes)).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')

/** A random, URL-safe string with `bytes` of entropy. */
export function randomToken (bytes = 32): string {
  return base64Url(crypto.getRandomValues(new Uint8Array(bytes)))
}

/** RFC 7636's S256: base64url of the verifier's SHA-256. */
export async function s256 (verifier: string): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(verifier))
  return base64Url(new Uint8Array(digest))
}

/** RFC 7636: the verifier stays here, the S256 challenge goes to Cognito. */
export async function createPkce (): Promise<{ verifier: string, challenge: string }> {
  const verifier = randomToken(32)
  return { verifier, challenge: await s256(verifier) }
}

// ── The redirect ────────────────────────────────────────────────────────────

export interface CognitoDomainConfig {
  /** `auth.dev.matchpoint-health.com`, without a scheme. */
  domain: string
  clientId: string
  /** Where Cognito sends the browser back. Must be on the app client's callback list. */
  redirectUri: string
}

export function authorizeUrl (
  config: CognitoDomainConfig,
  provider: SignInRoute,
  state: string,
  challenge: string
): string {
  const params = new URLSearchParams({
    identity_provider: COGNITO_PROVIDER_NAMES[provider],
    response_type: 'code',
    client_id: config.clientId,
    redirect_uri: config.redirectUri,
    scope: 'openid email profile',
    state,
    code_challenge: challenge,
    code_challenge_method: 'S256'
  })
  return `https://${config.domain}/oauth2/authorize?${params.toString()}`
}

// ── What this tab remembers across the redirect ─────────────────────────────

const PENDING_KEY = 'match-point.federated-sign-in'

export interface PendingSignIn {
  state: string
  verifier: string
  /** Which route, and so which pool the code is exchanged with: `staff` is the staff pool. */
  provider: SignInRoute
  /** Where the guard was sending the person before sign-in interrupted. */
  returnTo: string | null
  /**
   * This attempt is the automatic second one, after the first linked an existing
   * account and bounced (`isLinkingBounce`). Only one is ever made.
   */
  afterLinking: boolean
}

/**
 * Session storage, not local: it is per tab and dies with it, which is the
 * lifetime of one sign-in attempt, and a verifier has no business outliving that.
 */
export function rememberPending (pending: PendingSignIn, storage: Storage = sessionStorage): void {
  storage.setItem(PENDING_KEY, JSON.stringify(pending))
}

/**
 * The attempt this callback belongs to, consumed — or null if there is none or
 * the state does not match. Consumed either way, so a code cannot be replayed
 * against the same verifier.
 */
export function takePending (state: string | null, storage: Storage = sessionStorage): PendingSignIn | null {
  const raw = storage.getItem(PENDING_KEY)
  storage.removeItem(PENDING_KEY)
  if (!raw || !state) return null

  try {
    const pending = JSON.parse(raw) as Partial<PendingSignIn>
    return typeof pending.state === 'string' &&
      pending.state === state &&
      typeof pending.verifier === 'string' &&
      isSignInRoute(pending.provider)
      ? {
          state: pending.state,
          verifier: pending.verifier,
          provider: pending.provider,
          returnTo: typeof pending.returnTo === 'string' ? pending.returnTo : null,
          afterLinking: pending.afterLinking === true
        }
      : null
  } catch {
    return null
  }
}

// ── The exchange ────────────────────────────────────────────────────────────

export interface CognitoTokens {
  idToken: string
  accessToken: string
  refreshToken: string
}

/** The code for tokens, at `/oauth2/token`. */
export async function exchangeCode (
  config: CognitoDomainConfig,
  code: string,
  verifier: string,
  fetchImpl: typeof fetch = fetch
): Promise<CognitoTokens> {
  const response = await fetchImpl(`https://${config.domain}/oauth2/token`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: new URLSearchParams({
      grant_type: 'authorization_code',
      client_id: config.clientId,
      code,
      redirect_uri: config.redirectUri,
      code_verifier: verifier
    }).toString()
  })

  if (!response.ok) {
    throw new Error(`The token exchange answered ${response.status}.`)
  }

  const body = await response.json() as Record<string, unknown>
  const { id_token: idToken, access_token: accessToken, refresh_token: refreshToken } = body

  if (typeof idToken !== 'string' || typeof accessToken !== 'string' || typeof refreshToken !== 'string') {
    throw new Error('The token exchange returned no tokens.')
  }

  return { idToken, accessToken, refreshToken }
}

/**
 * Whether a callback error is Cognito reporting that it just linked this
 * identity onto an existing account (ADR 0012 decision 5) rather than a failure.
 *
 * The pre-sign-up trigger links the provider identity onto the password account
 * with the same verified address, and Cognito then refuses to finish the sign-in
 * that triggered it, because the user it was about to create now exists as a
 * link. The next attempt signs straight in to the existing account. So the
 * callback starts that attempt itself, once, instead of showing the person an
 * error about a username they have never seen.
 */
export function isLinkingBounce (description: string | null): boolean {
  return description != null && /already found an entry for username/i.test(description)
}

/**
 * Whether this failed callback earns the one automatic retry: it was an attempt
 * this tab started, it was not itself the retry, and Cognito said it linked.
 * A second bounce is an error, never a loop.
 */
export function shouldRetryAfterLinking (failed: PendingSignIn | null, description: string | null): failed is PendingSignIn {
  return failed != null && !failed.afterLinking && isLinkingBounce(description)
}

/**
 * The message for a provider's refusal, which arrives on the callback as
 * `?error=` rather than as a code. Canceling at Google is the common one and is
 * not a failure worth alarming anybody about.
 */
export function callbackErrorMessage (error: string, description: string | null): string {
  if (error === 'access_denied') {
    return 'Sign-in was canceled.'
  }
  return description?.trim()
    ? `Sign-in did not complete: ${description.trim()}`
    : 'Sign-in did not complete.'
}
